import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import {
  Document_FieldsFragment,
  ShareDocument_GetSharedDocumentsByDocumentIdDocument,
  ShareDocument_GetSharedDocumentsByDocumentIdQuery,
  useShareDocument_DeleteMutation,
  useShareDocument_GetSharedDocumentsByDocumentIdLazyQuery,
  useShareDocument_ShareMutation,
} from 'queries';

import Modal from 'common/modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import DocumentShare from './document-share';

type Props = {
  setShowModal: (value: boolean) => void;
  showModal: boolean;
  selected: Document_FieldsFragment[];
  onClose?: () => void;
};

const ShareSuccess = () => {
  return (
    <div style={{ paddingBottom: '50px' }}>
      <img src="/checkmark-icon.svg" alt="Success!" />
      <Typography>Your invitation(s) have been sent!</Typography>
    </div>
  );
};

export default function DocumentShareModal({ setShowModal, showModal, selected, onClose }: Props) {
  const { portalId } = useParams<Record<string, string>>();

  const [success, setSuccess] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [comment, setComment] = useState<string>('');

  const [
    getSharedDocumentsByDocumentId,
    { data: sharedDocumentData, loading: sharedDocumentLoading },
  ] = useShareDocument_GetSharedDocumentsByDocumentIdLazyQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting shared document: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [deleteSharedDocument] = useShareDocument_DeleteMutation({
    refetchQueries: [
      {
        query: ShareDocument_GetSharedDocumentsByDocumentIdDocument,
        variables: { documentId: selected[0]?.id },
      },
    ],
    onError: (e) => {
      showAlert({
        message: 'Error deleting shared document: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [shareDocument, { loading: shareDocumentExecuting }] = useShareDocument_ShareMutation({
    onCompleted: () => {
      setEmails([]);
      setSuccess(true);
    },
    update: (store, { data: shareData }) => {
      const cachedSharedDocumentData =
        store.readQuery<ShareDocument_GetSharedDocumentsByDocumentIdQuery>({
          query: ShareDocument_GetSharedDocumentsByDocumentIdDocument,
          variables: { documentId: selected[0].id },
        });

      if (cachedSharedDocumentData) {
        const files = cachedSharedDocumentData
          ? [...cachedSharedDocumentData.data, ...shareData!.data]
          : shareData!.data;

        store.writeQuery<ShareDocument_GetSharedDocumentsByDocumentIdQuery>({
          query: ShareDocument_GetSharedDocumentsByDocumentIdDocument,
          data: {
            data: files,
          },
          variables: { documentId: selected[0].id },
        });
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error sharing document: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  useEffect(() => {
    if (selected.length) {
      getSharedDocumentsByDocumentId({ variables: { documentId: selected[0].id } });
    }
  }, [selected, getSharedDocumentsByDocumentId]);

  return (
    <Modal
      setShowModal={setShowModal}
      showModal={showModal}
      onActionClick={() => {
        shareDocument({
          variables: {
            input: {
              portalId,
              documentId: selected[0].id,
              shareToEmails: emails,
              comment,
            },
          },
        });
      }}
      onExitClick={() => {
        if (onClose) {
          onClose();
        }
        setShowModal(false);
        setSuccess(false);
      }}
      content={
        success ? (
          <ShareSuccess />
        ) : (
          <DocumentShare
            shared={sharedDocumentData?.data}
            emails={emails}
            setEmails={setEmails}
            setComment={setComment}
            onDelete={(id: string) =>
              deleteSharedDocument({
                variables: {
                  input: {
                    portalId,
                    sharedDocumentId: id,
                  },
                },
              })
            }
          />
        )
      }
      title={success ? 'Success!' : 'Share Your Documents'}
      actionVerb="Send Invite"
      loading={sharedDocumentLoading}
      maxWidth="lg"
      showActions={!success}
      actionDisabled={emails.length < 1 || shareDocumentExecuting}
    />
  );
}
